import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { type HttpError, isHttpError } from '@/api/http/errors'
import { queryKeys } from '@/api/query-keys'
import { dictionaryService } from '@/api/services/dictionary.service'
import type { AddDictionaryWordOutcome, DictionaryResponse } from '@/api/types/dictionary'
import { logger } from '@/utils/logger'

const CONFLICT = 409
const FORBIDDEN = 403
const SERVICE_UNAVAILABLE = 503
const SESSION_EXPIRED_CODE = 'DICTIONARY_SESSION_EXPIRED'

/** A 403 is the session ending only when the backend says so; any other 403 is a plain failure. */
async function isSessionExpired(error: HttpError): Promise<boolean> {
  const body = (await error.response?.json().catch(() => null)) as { code?: unknown } | null

  return body?.code === SESSION_EXPIRED_CODE
}

/**
 * The spelling the dictionary already holds, from the 409 body. The backend refuses only an exact
 * duplicate (words that differ only by capitalization are separate entries), so this is the typed
 * word itself; it is read anyway so the cache shows what the server holds.
 */
async function readStoredWord(error: HttpError): Promise<string | undefined> {
  const body = (await error.response?.json().catch(() => null)) as { storedWord?: unknown } | null

  return typeof body?.storedWord === 'string' && body.storedWord !== ''
    ? body.storedWord
    : undefined
}

/**
 * Adds one custom word and waits for the backend before touching the cache, so a failed save never
 * shows the word. A duplicate (409) means the word is already on the server, possibly added by
 * someone else a moment ago, so the stored spelling (`storedWord` in the 409) is put in the cache
 * and it is reported as a duplicate. When the
 * word is shown, a read already running is cancelled so it cannot overwrite it, and the dictionary
 * is read again so other editors' changes still show.
 */
export function useAddDictionaryWord() {
  const queryClient = useQueryClient()

  return useCallback(
    async (word: string): Promise<AddDictionaryWordOutcome> => {
      const showWord = (written: string) => {
        // A read already running was sent before this word was saved, so it would come back
        // without it and overwrite it. Cancel it before showing the word.
        void queryClient.cancelQueries({ queryKey: queryKeys.dictionary.all })

        queryClient.setQueryData<DictionaryResponse>(queryKeys.dictionary.all, (current) =>
          current && !current.customWords.includes(written)
            ? { ...current, customWords: [...current.customWords, written] }
            : current
        )

        // Read again so a change another editor made in the meantime still shows. The cancelled
        // read may have been carrying it.
        void queryClient.invalidateQueries({ queryKey: queryKeys.dictionary.all })
      }

      try {
        const result = await dictionaryService.addWord(word)

        showWord(result.word)

        return { status: 'added', word: result.word }
      } catch (error) {
        if (isHttpError(error) && error.status === CONFLICT) {
          const storedWord = await readStoredWord(error)

          showWord(storedWord ?? word)

          return { status: 'duplicate', storedWord }
        }

        // The backend is busy with another dictionary write: worth trying again in a moment.
        if (isHttpError(error) && error.status === SERVICE_UNAVAILABLE) return { status: 'busy' }

        if (isHttpError(error) && error.status === FORBIDDEN && (await isSessionExpired(error))) {
          return { status: 'sessionExpired' }
        }

        logger.error('Adding a dictionary word failed', error)

        return { status: 'failed' }
      }
    },
    [queryClient]
  )
}
